<template>
  <div class="admin-list">
    <PageTitle :title="$t('page.branchSetting.title')" :btn="$t('common.button.create.text')" @btnClick="openDialog('create')" />
    <FiltersContainer>
      <BaseElInput v-model="nameSearch" clearable :placeholder="$t('branchSetting.searchName.placeholder')" @keypress.enter.native="refresh(true)" @clear="refresh(true)">
        <i slot="suffix" class="el-input__icon el-icon-search" @click="refresh(true)" />
      </BaseElInput>
    </FiltersContainer>

    <section>
      <BaseTable v-loading="loading" :data="shopList" :empty-text="$t('common.table.empty.text')">
        <EmptyBlock slot="empty" />
        <BaseElTableColumn
          v-if="enableOrgBenefitSharing && headquarterFeatureEnabled"
          :label="$t('branchSetting.table.headquarter.title')"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ isHeadquarter(scope.row) ? $t('branchSetting.table.headquarter.label') : $t('branchSetting.table.headquarter.normalLabel') }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="$t('branchSetting.table.name.title')" prop="name" align="center" />
        <BaseElTableColumn :label="$t('branchSetting.table.phone.title')" prop="phone" align="center" />
        <BaseElTableColumn :label="$t('branchSetting.table.currency.title')" prop="currency" align="center" />
        <BaseElTableColumn :label="$t('branchSetting.table.timezone.title')" prop="timezone" align="center">
          <template slot-scope="scope">
            {{ shopTimezone(scope.row) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="$t('branchSetting.table.address.title')" prop="address" align="center" />
        <BaseElTableColumn :label="$t('branchSetting.table.email.title')" prop="email" align="center">
          <template slot-scope="scope">
            {{ scope.row.email || '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="$t('branchSetting.table.contract.title')" align="center">
          <template slot-scope="scope">
            {{ shopContractName(scope.row) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn fixed="right" :label="$t('common.table.action.text')" width="220" align="center">
          <template slot-scope="scope">
            <div class="flex items-center justify-center gap-[8px]">
              <TableEditBtnGroup
                :hideDelete="!canDeleteShop"
                :deleteDisabled="scope.row.isHeadquarter || isHeadquarter(scope.row)"
                @edit="openDialog('update', scope.row)"
                @delete="openDeleteDialog(scope.row)"
              />
              <BaseElButton
                v-if="showSetHeadquarterAction(scope.row)"
                type="text"
                class="text-primary-100 underline"
                @click="onSetHeadquarter(scope.row)"
              >
                {{ $t('branchSetting.table.setHeadquarter.text') }}
              </BaseElButton>
            </div>
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="shopsCount"
        @pageChange="changePage"
      />
    </section>

    <!-- Dialog -->
    <EditBranchShopModal
      v-if="showDialog"
      :selectRow="selectRow"
      @close="showDialog = false"
      @refresh="refresh"
    />

    <DeleteDialog
      v-if="deleteRow"
      :title="$t('branchSetting.dialog.delete.title')"
      :content="$t('branchSetting.dialog.delete.content', { shopName: deleteRow.name })"
      :loading="deleteLoading"
      @close="deleteRow = null"
      @delete="deleteShop"
    />

    <SetHeadquarterModal
      v-if="showSetHeadquarterDialog"
      :shop="setHeadquarterConfirmRow"
      :orgId="orgId"
      @close="showSetHeadquarterDialog = false"
      @success="onSetHeadquarterSuccess"
      @error="onSetHeadquarterError"
    />
  </div>
</template>

<script>
import EmptyBlock from '@/components/EmptyBlock.vue'
import DeleteDialog from '@/components/Dialog/DeleteDialog.vue'
import { GetShops, GetShopsCount, GetShopById, GetIndustryCategories, DeleteShop } from '@/api/shop'
import { GetShopBenefitSharingHeadquarter } from '@/api/shopBenefitSharing'
import EditBranchShopModal from './components/EditBranchShopModal.vue'
import SetHeadquarterModal from './components/SetHeadquarterModal.vue'

// Utils
import { pageStartIndex } from '@/utils/table'
import jpCities from '@/assets/data/japanCities'

import { currencyOptions, timezoneOptions } from '@/config/branchSetting'
import { get } from 'lodash'
import store from '@/store'
import { computed, ref, provide } from 'vue'
import { useOrg } from '@/use/useOrg'
import { usePermissions } from '@/use/permissions'

export default {
  name: 'BranchShopList',
  components: { EmptyBlock, DeleteDialog, EditBranchShopModal, SetHeadquarterModal },
  setup () {
    // 組織層頁面沒有店家方案，只看帳號權限
    const { checkActionByUserFeatures } = usePermissions()
    // GIMMATEK 一間分店代表一位服務人員，可在服務人員設定刪除（CNCY-10959）
    const canDeleteShop = computed(() => checkActionByUserFeatures('admin.shop.destroy'))
    const japanCities = ref(jpCities)
    const { dashboardConfig, orgId } = useOrg()
    const enableOrgBenefitSharing = computed(() => get(dashboardConfig.value, 'orgModulePermission.enableOrgBenefitSharing', false))
    const cities = ref([])
    const selectRow = ref(null)
    const isEdit = computed(() => get(selectRow.value, 'id'))
    const isOverseasShop = computed(() => get(store.state, 'org.isOverseasShop'))
    const showControl = computed(() => {
      const controls = {
        formItem: {
          timezone: true,
        },
        formItemHint: {
          timezone: true,
        },
      }

      if (get(dashboardConfig.value, 'orgModulePermission.enableShopTimezone')) {
        controls.formItem.timezone = true
      }
      if (isEdit.value) {
        controls.formItemHint.timezone = false
      }

      return controls
    })
    const industryCategories = ref([])

    const getIndustryCategories = async () => {
      const [res, err] = await GetIndustryCategories()
      if (err) {
        window.$message.error(err)
        return
      }
      industryCategories.value = res.industryCategories
    }
    const shopTimezone = (row) => {
      return get(timezoneOptions[row.timezone], 'label', '-')
    }

    provide('industryCategories', industryCategories)
    return {
      canDeleteShop,
      isOverseasShop,
      japanCities,
      cities,
      industryCategories,
      getIndustryCategories,
      selectRow,
      currencyOptions,
      timezoneOptions,
      shopTimezone,
      showControl,
      isEdit,
      orgId,
      enableOrgBenefitSharing,
    }
  },
  data: () => ({
    loading: false,
    btnLoading: false,
    headquarterLoading: false,
    userSearch: '',
    nameSearch: '',
    tableOptions: {
      page: 1,
      pageLimit: 10,
    },

    roleList: ['orgAdmin'],

    shopList: [],
    shopsCount: 0,
    headquarterData: null,
    headquarterFeatureEnabled: true,

    // Dialog
    showDialog: false,
    showSetHeadquarterDialog: false,
    setHeadquarterConfirmRow: null,
    dialogType: 'create',
    deleteRow: null,
    deleteLoading: false,
  }),

  computed: {
    pageStartIndex () {
      return pageStartIndex(this.tableOptions.page, this.tableOptions.pageLimit)
    },
    hasHeadquarter () {
      return get(this.headquarterData, 'hasHeadquarter', false)
    },
    headquarterShopId () {
      return this.resolveHeadquarterShopId(this.headquarterData)
    },
  },

  async mounted () {
    await this.refresh()
  },

  methods: {
    async refresh (search = false) {
      if (search) this.tableOptions.page = 1
      await Promise.allSettled([
        this.getShops(),
        this.getShopsCount(),
        this.getIndustryCategories(),
        this.getHeadquarter(),
      ])
    },
    showInput (typeList) {
      return !!typeList.includes(this.dialogType)
    },

    async changePage () {
      await this.getShops()
    },
    shopContractName (row) {
      return get(row, 'ContractPlan.name', '-')
    },

    openDialog (type, row) {
      this.dialogType = type
      this.showDialog = true
      this.selectRow = row
    },

    async findShop () {
      const [res, err] = await GetShopById(this.selectRow.id)
      if (err) return this.$message.error(err || err.msg)
      this.selectRow = res
    },

    resolveHeadquarterShopId (headquarterData) {
      return get(headquarterData, 'shopId') ||
        get(headquarterData, 'id') ||
        get(headquarterData, 'headquarterShopId') ||
        get(headquarterData, 'headquarter.shopId') ||
        get(headquarterData, 'headquarter.id') ||
        get(headquarterData, 'shop.shopId') ||
        get(headquarterData, 'shop.id') ||
        ''
    },

    isHeadquarter (row) {
      return this.headquarterShopId === row.id
    },

    showSetHeadquarterAction (row) {
      return this.enableOrgBenefitSharing && this.headquarterFeatureEnabled && !this.headquarterLoading && !this.hasHeadquarter && !!row.id
    },

    async getHeadquarter () {
      if (!this.orgId) return

      this.headquarterLoading = true
      const [res, err, rawErr] = await GetShopBenefitSharingHeadquarter({
        orgId: this.orgId,
      })
      this.headquarterLoading = false

      if (err) {
        if (get(rawErr, 'errorCode') === 'ORG_BENEFIT_SHARING_NOT_ENABLED') {
          this.headquarterFeatureEnabled = false
          this.headquarterData = null
          return
        }

        this.headquarterFeatureEnabled = true
        this.headquarterData = null
        window.$message.error(err)
        return
      }

      this.headquarterFeatureEnabled = true
      this.headquarterData = res
    },

    openDeleteDialog (row) {
      this.deleteRow = row
    },

    async deleteShop () {
      const { id } = this.deleteRow
      this.deleteLoading = true
      const [, err] = await DeleteShop({ shopId: id })
      this.deleteLoading = false
      if (err) return window.$message.error(err)

      // 刪除的分店從可選分店移除；目前選的就是它時取消選擇
      store.commit('user/SET_ALLOW_SHOP', (store.state.user.allowShop || []).filter((shop) => shop.id !== id))
      if (store.state.user.shop === id) store.commit('user/SET_NOW_SHOP', null)

      this.deleteRow = null
      window.$message.success(this.$t('branchSetting.message.deleteSuccess'))
      await this.refresh()
    },

    async onSetHeadquarter (row) {
      this.setHeadquarterConfirmRow = row
      this.showSetHeadquarterDialog = true
    },

    async onSetHeadquarterSuccess () {
      this.showSetHeadquarterDialog = false
      window.$message.success(this.$t('branchSetting.message.setHeadquarterSuccess'))
      await this.refresh()
    },

    async onSetHeadquarterError ({ err, errorCode }) {
      if (errorCode === 'HEADQUARTER_ALREADY_SET' || errorCode === 'SHOP_NOT_FOUND_IN_ORG') {
        await Promise.allSettled([
          this.getHeadquarter(),
          this.getShops(),
        ])
      }
      window.$message.error(err)
    },

    //= > 取得店家
    async getShops () {
      this.loading = true
      try {
        this.shopList = await GetShops({
          start: this.pageStartIndex,
          limit: this.tableOptions.pageLimit,
          name: (this.nameSearch === '') ? undefined : this.nameSearch,
        })
        this.shopList = this.shopList.map(shop => {
          if (shop.address) {
            shop.address = shop.address.replace(/,/g, '')
          }
          return shop
        })
        this.loading = false
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },

    //= > 取得總店家數量
    async getShopsCount () {
      try {
        this.shopsCount = await GetShopsCount({
          name: (this.nameSearch === '') ? undefined : this.nameSearch,
        })
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },

  },
}
</script>

<style scoped lang="scss">
::v-deep .grid .el-input {
  @apply w-auto;
}
</style>
